import type { TableColumnsType } from 'antd';
import {
  Alert,
  Button,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  MonitorCog,
  RefreshCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import {
  PUBLIC_APP_ORIGIN,
  TUTTI_SERVER_API_REACHABLE,
} from '@/config/domains';
import { UserLookupWidget } from '@/pages/AlertAttribution/UserLookupWidget';
import {
  tuttiComputerApi,
  tuttiComputerErrorMessage,
} from '@/services/tuttiComputerApi';
import { authStore } from '@/stores';
import type { TuttiComputer, TuttiComputerConnectionStatus } from '@/types';
import { TuttiComputerDrawer } from './TuttiComputerDrawer';

const PAGE_SIZE = 50;

function formatDateTime(value: string) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false });
}

function platformLabel(platform: string) {
  switch (platform.toLowerCase()) {
    case 'darwin':
      return 'macOS';
    case 'windows':
      return 'Windows';
    case 'linux':
      return 'Linux';
    default:
      return platform || '-';
  }
}

function StatusTag({ status }: { status: TuttiComputerConnectionStatus }) {
  if (status === 'online') {
    return <Tag color="success">在线</Tag>;
  }
  if (status === 'offline') {
    return <Tag>离线</Tag>;
  }
  return <Tag color="warning">未知</Tag>;
}

export function TuttiComputersPage() {
  const canLookupUsers = authStore.can(ADMIN_PERMISSIONS.alertAttributionRead);
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);
  const [computers, setComputers] = useState<TuttiComputer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState('');

  const [nameInput, setNameInput] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [ownerInput, setOwnerInput] = useState('');
  const [ownerQuery, setOwnerQuery] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<
    TuttiComputerConnectionStatus | 'all'
  >('all');
  const [platform, setPlatform] = useState('');
  const [versionInput, setVersionInput] = useState('');
  const [hostArtifactVersion, setHostArtifactVersion] = useState('');

  const [pageNumber, setPageNumber] = useState(1);
  const [pageTokens, setPageTokens] = useState<string[]>(['']);
  const [nextPageToken, setNextPageToken] = useState('');

  const [selectedComputerId, setSelectedComputerId] = useState('');
  const [selectedComputer, setSelectedComputer] =
    useState<TuttiComputer | null>(null);

  const resetPagination = useCallback(() => {
    setPageNumber(1);
    setPageTokens(['']);
    setNextPageToken('');
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const value = nameInput.trim();
      if (value !== nameQuery) {
        setNameQuery(value);
        resetPagination();
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [nameInput, nameQuery, resetPagination]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const value = ownerInput.trim();
      if (value !== ownerQuery) {
        setOwnerQuery(value);
        resetPagination();
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [ownerInput, ownerQuery, resetPagination]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const value = versionInput.trim();
      if (value !== hostArtifactVersion) {
        setHostArtifactVersion(value);
        resetPagination();
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [hostArtifactVersion, resetPagination, versionInput]);

  const currentPageToken = pageTokens[pageNumber - 1] ?? '';

  const loadComputers = useCallback(
    async ({ background = false }: { background?: boolean } = {}) => {
      // No tutti-server route matches this console's API origin, so the request
      // would fall through to another service and fail for a reason that has
      // nothing to do with the device list. Do not send it.
      if (!TUTTI_SERVER_API_REACHABLE) {
        return;
      }
      if (background && loadingRef.current) {
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      loadingRef.current = true;
      if (!background) {
        setLoading(true);
      }
      try {
        const result = await tuttiComputerApi.list({
          nameQuery: nameQuery || undefined,
          ownerQuery: ownerQuery || undefined,
          connectionStatus:
            connectionStatus === 'all' ? undefined : connectionStatus,
          platform: platform || undefined,
          hostArtifactVersion: hostArtifactVersion || undefined,
          pageSize: PAGE_SIZE,
          pageToken: currentPageToken || undefined,
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setComputers(result.computers);
        setNextPageToken(result.nextPageToken);
        setError('');
        setLastRefreshedAt(new Date().toISOString());
      } catch (loadError) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const errorMessage = tuttiComputerErrorMessage(
          loadError,
          '加载 Tutti Computer 失败',
        );
        setError(errorMessage);
        if (!background) {
          message.error(errorMessage);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [
      connectionStatus,
      currentPageToken,
      hostArtifactVersion,
      nameQuery,
      ownerQuery,
      platform,
    ],
  );

  useEffect(() => {
    void loadComputers();
  }, [loadComputers]);

  useEffect(() => {
    if (!TUTTI_SERVER_API_REACHABLE) {
      return;
    }
    const timer = window.setInterval(() => {
      void loadComputers({ background: true });
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [loadComputers]);

  const openComputer = useCallback((computer: TuttiComputer) => {
    setSelectedComputer(computer);
    setSelectedComputerId(computer.computerId);
  }, []);

  const columns: TableColumnsType<TuttiComputer> = useMemo(
    () => [
      {
        title: '设备',
        key: 'computer',
        width: 260,
        render: (_value, computer) => (
          <div className="min-w-0">
            <Button
              type="link"
              className="h-auto max-w-full px-0 py-0 text-left font-medium"
              onClick={(event) => {
                event.stopPropagation();
                openComputer(computer);
              }}
            >
              <span className="truncate">
                {computer.displayName || computer.reportedName || '未命名设备'}
              </span>
            </Button>
            {computer.displayName && computer.reportedName ? (
              <div className="truncate text-xs text-muted-foreground">
                {computer.reportedName}
              </div>
            ) : null}
            <Tooltip title={computer.computerId}>
              <div className="truncate font-mono text-xs text-muted-foreground">
                {computer.computerId}
              </div>
            </Tooltip>
          </div>
        ),
      },
      {
        title: '用户',
        key: 'owner',
        width: 230,
        render: (_value, computer) => (
          <div className="min-w-0">
            <div className="truncate">
              {computer.owner.name || computer.owner.email || '-'}
            </div>
            {computer.owner.name && computer.owner.email ? (
              <div className="truncate text-xs text-muted-foreground">
                {computer.owner.email}
              </div>
            ) : null}
            {!computer.owner.name && !computer.owner.email ? (
              <Tooltip title={computer.owner.userId}>
                <div className="truncate font-mono text-xs text-muted-foreground">
                  {computer.owner.userId || '-'}
                </div>
              </Tooltip>
            ) : null}
          </div>
        ),
      },
      {
        title: '状态',
        dataIndex: 'connectionStatus',
        width: 90,
        render: (value: TuttiComputerConnectionStatus) => (
          <StatusTag status={value} />
        ),
      },
      {
        title: '系统',
        key: 'platform',
        width: 130,
        render: (_value, computer) => (
          <div>
            <div>{platformLabel(computer.platform)}</div>
            <div className="text-xs text-muted-foreground">
              {computer.arch || '-'}
            </div>
          </div>
        ),
      },
      {
        title: '版本',
        key: 'versions',
        width: 190,
        render: (_value, computer) => (
          <div className="space-y-0.5 text-sm">
            <div>
              Host Artifact{' '}
              <span className="font-mono">
                {computer.hostArtifactVersion || '-'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Companion {computer.companionVersion || '-'}
            </div>
          </div>
        ),
      },
      {
        title: '绑定时间',
        dataIndex: 'boundAt',
        width: 175,
        render: formatDateTime,
      },
      {
        title: '最后在线',
        dataIndex: 'lastSeenAt',
        width: 175,
        render: formatDateTime,
      },
      {
        title: '操作',
        key: 'actions',
        fixed: 'right',
        width: 80,
        render: (_value, computer) => (
          <Button
            type="link"
            className="px-0"
            onClick={(event) => {
              event.stopPropagation();
              openComputer(computer);
            }}
          >
            查看
          </Button>
        ),
      },
    ],
    [openComputer],
  );

  const handleNextPage = () => {
    if (!nextPageToken) {
      return;
    }
    setPageTokens((current) => {
      const next = current.slice(0, pageNumber);
      next[pageNumber] = nextPageToken;
      return next;
    });
    setPageNumber((current) => current + 1);
  };

  const handleResetFilters = () => {
    setNameInput('');
    setNameQuery('');
    setOwnerInput('');
    setOwnerQuery('');
    setConnectionStatus('all');
    setPlatform('');
    setVersionInput('');
    setHostArtifactVersion('');
    resetPagination();
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MonitorCog className="h-5 w-5 text-accent" />
              <h1 className="text-lg font-semibold">Tutti Computer</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              查看已绑定设备的连接与 Host Artifact
              版本状态，并向单台在线设备召回 desktopd 运行日志。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {lastRefreshedAt ? (
              <span className="text-xs text-muted-foreground">
                更新于 {formatDateTime(lastRefreshedAt)}
              </span>
            ) : null}
            <Button
              icon={<RefreshCcw className="h-4 w-4" />}
              loading={loading}
              disabled={!TUTTI_SERVER_API_REACHABLE}
              onClick={() => void loadComputers()}
            >
              刷新
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="border-b border-border p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <SlidersHorizontal className="h-4 w-4" />
            设备筛选
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(200px,1fr)_minmax(200px,1fr)_150px_150px_minmax(180px,0.8fr)_auto]">
            <Input
              value={nameInput}
              allowClear
              prefix={<Search className="h-4 w-4 text-muted-foreground" />}
              placeholder="搜索设备名"
              aria-label="搜索设备名"
              onChange={(event) => setNameInput(event.target.value)}
            />
            <Input
              value={ownerInput}
              allowClear
              prefix={<Search className="h-4 w-4 text-muted-foreground" />}
              placeholder="搜索用户名/邮箱"
              aria-label="搜索用户名/邮箱"
              onChange={(event) => setOwnerInput(event.target.value)}
            />
            <Select
              value={connectionStatus}
              aria-label="连接状态"
              options={[
                { label: '全部状态', value: 'all' },
                { label: '在线', value: 'online' },
                { label: '离线', value: 'offline' },
              ]}
              onChange={(value) => {
                setConnectionStatus(value);
                resetPagination();
              }}
            />
            <Select
              value={platform}
              aria-label="设备平台"
              options={[
                { label: '全部平台', value: '' },
                { label: 'macOS', value: 'darwin' },
                { label: 'Windows', value: 'windows' },
                { label: 'Linux', value: 'linux' },
              ]}
              onChange={(value) => {
                setPlatform(value);
                resetPagination();
              }}
            />
            <Input
              value={versionInput}
              allowClear
              placeholder="Host Artifact 版本"
              aria-label="Host Artifact 版本"
              onChange={(event) => setVersionInput(event.target.value)}
            />
            <Button onClick={handleResetFilters}>重置</Button>
          </div>
        </div>

        {!TUTTI_SERVER_API_REACHABLE ? (
          <Alert
            className="m-4 mb-0"
            type="warning"
            showIcon
            message="当前控制台无法访问 Tutti Computer 接口"
            description={`Tutti Computer 接口只发布在 tutti.sh，${PUBLIC_APP_ORIGIN} 的 API 请求不会路由到该服务。请改用 https://admin.tutti.sh 管理设备。`}
          />
        ) : null}

        {error ? (
          <Alert
            className="m-4 mb-0"
            type="error"
            showIcon
            message="设备列表加载失败"
            description={error}
            action={
              <Button size="small" onClick={() => void loadComputers()}>
                重试
              </Button>
            }
          />
        ) : null}

        <Table<TuttiComputer>
          rowKey="computerId"
          columns={columns}
          dataSource={computers}
          loading={loading}
          pagination={false}
          scroll={{ x: 1330 }}
          locale={{ emptyText: '没有符合筛选条件的 Computer' }}
          rowClassName="cursor-pointer"
          onRow={(computer) => ({
            onClick: () => openComputer(computer),
          })}
        />

        <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            第 {pageNumber} 页 · 本页 {computers.length} 台 · 每页最多{' '}
            {PAGE_SIZE} 台
          </span>
          <Space>
            <Button
              disabled={pageNumber <= 1 || loading}
              onClick={() => setPageNumber((current) => current - 1)}
            >
              上一页
            </Button>
            <Button
              disabled={!nextPageToken || loading}
              onClick={handleNextPage}
            >
              下一页
            </Button>
          </Space>
        </div>
      </div>

      <TuttiComputerDrawer
        computerId={selectedComputerId}
        initialComputer={selectedComputer}
        open={Boolean(selectedComputerId)}
        onClose={() => {
          setSelectedComputerId('');
          setSelectedComputer(null);
        }}
      />
      {canLookupUsers ? <UserLookupWidget includeSensitive={false} /> : null}
    </div>
  );
}
